/**
 * API client functions for admin search endpoints.
 *
 * Provides unified search across Wikidata and canonical entities
 * for use in the resolve freetext dialog.
 */

import apiClient from './client'

/**
 * Wikidata composer search result.
 */
export interface WikidataComposerResult {
  wikidata_id: string
  name: string
  birth_year: number | null
  death_year: number | null
}

/**
 * Canonical composer search result.
 */
export interface CanonicalComposerResult {
  canonical_composer_id: string
  name: string
  birth_year: number | null
  death_year: number | null
  notes: string | null
  piece_count: number
}

/**
 * Combined composer search results from Wikidata and canonical composers.
 */
export interface ComposerSearchResult {
  wikidata_composers: WikidataComposerResult[]
  canonical_composers: CanonicalComposerResult[]
}

/**
 * Wikidata work search result.
 */
export interface WikidataWorkResult {
  wikidata_id: string
  title: string
  catalog_number: string | null
  instrumentation: string | null
  year: number | null
}

/**
 * Canonical piece search result.
 */
export interface CanonicalPieceResult {
  canonical_piece_id: string
  work_title: string | null
  composer_name: string
  suggestion_count: number
}

/**
 * Combined work search results from Wikidata and canonical pieces.
 */
export interface WorkSearchResult {
  wikidata_works: WikidataWorkResult[]
  canonical_pieces: CanonicalPieceResult[]
}

/**
 * Search composers across Wikidata and canonical composers.
 */
export async function searchComposers(
  query: string,
  limit?: number,
): Promise<ComposerSearchResult> {
  const params = new URLSearchParams({ q: query })
  if (limit !== undefined) {
    params.set('limit', String(limit))
  }
  return await apiClient.get<ComposerSearchResult>(`/admin/search/composers?${params.toString()}`)
}

/**
 * Search works scoped to a specific composer.
 */
export async function searchWorks(
  query: string,
  composerId: string,
  composerType: 'wikidata' | 'canonical',
  limit?: number,
): Promise<WorkSearchResult> {
  const params = new URLSearchParams({
    q: query,
    composerId,
    composerType,
  })
  if (limit !== undefined) {
    params.set('limit', String(limit))
  }
  return await apiClient.get<WorkSearchResult>(`/admin/search/works?${params.toString()}`)
}
